/**
 * Price touch market panel (#786): underlying perp, target, live distance from the current perp mark to the target,
 * the touch-by deadline and how the market resolves (TWAP touch, settles early). Shown on the market page for
 * priceTouch* outcomes only. The distance is a hint relative to NOW: the price at listing (and so the direction of the
 * market) is not on-chain, and the venue, not this app, decides whether the target was touched.
 */
import { formatWithUnit, touchDistance, type TouchInfo } from '../describe';
import { priceUnit } from '../describe/format';
import { CHIP, Countdown, LocalTime } from '../describe/ui';
import { usePerpMark } from '../hl/perpMark';
import { whenLabel } from '../describe/when';
import { useT } from '../i18n/useT';

export function TouchBadge({ className = '' }: { className?: string }) {
  const t = useT();
  return (
    <span
      className={`${CHIP} rounded-full border border-warn/50 px-1.5 text-[10px] font-medium uppercase tracking-wide text-warn ${className}`}
      title={t('touch.badge_title')}
      data-testid="touch-badge"
    >
      {t('touch.badge')}
    </span>
  );
}

/** describe/ resolvesLabel id (not display text: Countdown translates it). */
const DEADLINE = 'Deadline';

const DIR_CLS = { up: 'text-yes', down: 'text-no', at: 'text-fg' } as const;

export default function TouchPanel({ touch, venue }: { touch: TouchInfo; venue?: string }) {
  const { mark, error } = usePerpMark(touch.perp);
  const t = useT();
  const dist = mark ? touchDistance(touch.target, mark) : null;
  const unit = priceUnit(touch.perp, touch.priceSource);
  return (
    <section className="card space-y-3 p-4" aria-labelledby="touch-h" data-testid="touch-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="touch-h" className="text-sm font-semibold">
          {t('touch.title')}
        </h2>
        <TouchBadge />
        <span className="text-xs text-warn" data-testid="touch-early-note">
          {t('touch.early')}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted">{t('touch.perp')}</dt>
          <dd className="num font-medium" data-testid="touch-perp" translate="no">
            {touch.perp}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted">{t('touch.target')}</dt>
          <dd className="num font-medium" data-testid="touch-target" translate="no">
            {touch.targetLabel}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted">{t('touch.mark')}</dt>
          <dd className="num font-medium" data-testid="touch-mark">
            {mark ? formatWithUnit(mark, unit) : error ? t('touch.mark_unavailable') : '…'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted">{t('touch.distance')}</dt>
          <dd
            className={`num font-medium ${dist ? DIR_CLS[dist.direction] : 'text-muted'}`}
            data-testid="touch-distance"
            translate="no"
          >
            {dist ? dist.label : '—'}
          </dd>
        </div>
      </dl>
      <p className="text-xs text-muted" data-testid="touch-deadline">
        {whenLabel('Deadline')} <LocalTime date={touch.deadline} /> · <Countdown date={touch.deadline} label={DEADLINE} />
      </p>
      <div className="space-y-1.5 rounded-md bg-surface-2/50 p-3 text-xs text-fg/85" data-testid="touch-rules">
        <p className="font-semibold text-fg">{t('touch.how')}</p>
        <p data-testid="touch-rule" translate="no">
          {touch.rule}
        </p>
        <p>{touch.twapSeconds ? t('touch.twap', { seconds: Number(touch.twapSeconds) }) : t('touch.twap_unknown')}</p>
        <p>{venue ? t('touch.venue', { venue }) : t('touch.venue_unknown')}</p>
      </div>
    </section>
  );
}
